/**
 * Category Tiles block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 *
 * Two jobs.
 *
 * The entrance: the motion itself is CSS, so the global prefers-reduced-motion
 * guard in main.scss covers it without a second check here — this file only
 * decides *when* it plays. The row renders complete and in its end state; the
 * attribute below is what opts it into the entrance, and a browser without
 * IntersectionObserver never gets it and simply shows the finished row.
 *
 * And the two arrows over a line longer than its frame. The line is an ordinary
 * scrolling box, so a finger, a trackpad and a wheel all reach it without this
 * file; the arrows are what a pointer gets instead.
 */

import './index.scss';

/**
 * Wire one row's arrows to the line under them.
 *
 * Each arrow shows only while there is somewhere to go that way, which is also
 * what keeps both off a row that fits. A step is one tile and the gap after it,
 * measured from the two the row actually rendered rather than from a number
 * written down twice.
 *
 * @param {HTMLElement} row The `.category-tiles` element.
 * @returns {void}
 */
function setupSteps(row) {
    const line = row.querySelector('.category-tiles__items');
    const steps = [...row.querySelectorAll('[data-tiles-step]')];

    if (!line || !steps.length) {
        return;
    }

    const items = [...line.querySelectorAll('.category-tiles__item')];

    /**
     * Show each arrow only while the line can move that way.
     *
     * @returns {void}
     */
    const sync = () => {
        /* A line that fits reports a scroll width a pixel or two over its own
         * from rounding, which would leave an arrow that steps nowhere. */
        const over = line.scrollWidth - line.clientWidth > 2;
        const at = line.scrollLeft;

        for (const step of steps) {
            const forward = Number(step.dataset.tilesStep) > 0;

            step.hidden =
                !over || (forward ? at >= line.scrollWidth - line.clientWidth - 2 : at <= 2);
        }
    };

    for (const step of steps) {
        step.addEventListener('click', () => {
            const pitch =
                items.length > 1
                    ? items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
                    : line.clientWidth;

            line.scrollBy({
                left: pitch * Number(step.dataset.tilesStep),
                behavior: 'smooth',
            });
        });
    }

    line.addEventListener('scroll', sync, { passive: true });

    /* The photographs are lazy, so the line's width is not final at parse time,
     * and the row is sized from the window. Both are the same question asked
     * again. */
    if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(line);
    }

    sync();
}

const rows = document.querySelectorAll('.category-tiles');

for (const row of rows) {
    setupSteps(row);
}

if (rows.length && 'IntersectionObserver' in window) {
    const revealer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    entry.target.setAttribute('data-reveal', 'in');

                    // Once seen, stay seen: scrolling back should not replay it.
                    revealer.unobserve(entry.target);
                }
            }
        },
        { rootMargin: '0px 0px -15% 0px' }
    );

    for (const row of rows) {
        row.setAttribute('data-reveal', 'pending');
        revealer.observe(row);
    }
}
